/**
 * SolumClient — the typed, entitlement-aware consumption boundary.
 *
 * A host (portal-v2, the standalone app, a connector) touches Solum ONLY through
 * this client: metamodel introspection (the typegen source), graph traversal,
 * and per-entity CRUD that resolves schema/table from the metamodel and goes
 * through RLS. Module surfaces are ENTITLEMENT-AWARE: an unentitled module is
 * ABSENT (`null`), never an error — the fail-open-to-read-only discipline surfaced
 * to the consumer (CLAUDE.md §11).
 */
import type { Transport, Row, SelectOptions, Page, WriteOptions } from './transport.js'
import {
  toSubjectQuery, subjectListArgs, project, SUBJECT_MAX_ROWS, type ReadAudit,
} from './subject.js'

export interface EntityTypeInfo {
  key: string
  module: string
  label: string
  schema_name: string
  table_name: string
  default_visibility?: string
  /**
   * Where a write has to go.
   *
   *   · `direct` — the API roles hold DML on the table and RLS is the gate, so
   *     `EntityHandle.create/update/delete` is the door.
   *   · `rpc`    — `core.enforce_write_doors()` has REVOKED that DML, and the
   *     only way in is the module's own governed function. A direct write here
   *     is answered `403 permission denied for table …`, every time, for
   *     everybody. It is not an authorization near-miss a different user would
   *     pass; the door is shut.
   *
   * This was already ON THE WIRE and merely undeclared: `core.describe_schema()`
   * builds each entity with `to_jsonb(e)`, so every column of `core.entity_type`
   * travels — including this one and `declared_write_door` (core 2026080319).
   * A consumer could not see it only because the type said it was not there.
   *
   * Optional because a deployment older than the column simply does not send it.
   * Treat ABSENT as `direct`, which is exactly what the registry resolves an
   * omitted `p_write_door` to — never as "unknown, so refuse", which would make
   * every old deployment read-only.
   */
  write_door?: 'direct' | 'rpc'
  /**
   * What the registration ASKED for, or null where it said nothing and
   * `write_door` was defaulted (core 2026080319). Consumers read `write_door`;
   * this exists so an omission stays distinguishable from a decision.
   */
  declared_write_door?: string | null
  /**
   * Whether this entity is ADMINISTRATION rather than business data — the
   * governed pre-query classification ROOT ruling 74071 §5 requires.
   *
   * Read by `apps/console/src/app/palette/paletteClass.ts` to decide what the
   * ordinary command palette may reach for BEFORE it queries anything. It is a
   * findability decision, never an authorization one: every door still
   * authorizes its own read (#2328 §8).
   *
   * OPTIONAL BECAUSE THE COLUMN DOES NOT EXIST YET. `core.entity_type` carries
   * no such column on this tree, and adding it is a core migration on a rung
   * this lane does not hold. It is declared here for the same reason
   * `write_door` was (see the note above it): `core.describe_schema()` builds
   * each entity with `to_jsonb(e)`, so every column of `core.entity_type`
   * travels — a consumer cannot see one only because the type says it is not
   * there. When the migration lands, this field starts arriving and nothing
   * else changes.
   *
   * `undefined` (an old box) and `null` (a box with the column, and a module
   * that has not declared) are deliberately distinct: only the second is
   * somebody's omission. Neither means `business` — see `paletteClass.ts` for
   * why the console nonetheless defaults that way today, and what closes it.
   */
  palette_class?: 'business' | 'administrative' | null
  /**
   * How a read of this entity is evidenced (core 0.386.0, #2444 slice 4).
   *
   *   · `subject` — every disclosure of a row is receipted. The API roles hold
   *     NO table SELECT: every read goes through `core.subject_get /
   *     subject_get_many / subject_list` (POST), each of which appends one
   *     `record.viewed` per row it returns. `EntityHandle` and `raw()` route
   *     there on their own; see `subject.ts`.
   *   · `request` — the request receipt covers it; direct reads as before.
   *   · `none` — declared, with a reason, as needing no read evidence.
   *
   * Travels for the same reason `write_door` does: `describe_schema()` builds
   * each entity with `to_jsonb(e)`. ABSENT (a deployment older than the column)
   * reads as `request` — the only value an old box can mean, since nothing on
   * it has closed a table.
   */
  read_audit?: ReadAudit
  /** Why `read_audit` is `none`; null otherwise. */
  read_audit_reason?: string | null
}

/** Is this entity read only through the governed subject doors? */
export function isSubjectEntity(info: Pick<EntityTypeInfo, 'read_audit'> | null | undefined): boolean {
  return info?.read_audit === 'subject'
}

/**
 * The governed subject read, shared by `EntityHandle` and `raw()` so the two
 * routes cannot disagree about what a subject read is. ONE POST per page.
 */
async function subjectList<T>(t: Transport, entity: string, opts: SelectOptions | undefined): Promise<T[]> {
  const q = toSubjectQuery(entity, opts)
  const rows = await t.rpcTable<Row>('subject_list', subjectListArgs(entity, q), 'core')
  return project<T>(Array.isArray(rows) ? rows : [], q.columns)
}

/**
 * A page of a subject entity, and the total ONLY where the page itself proves
 * it. There is no counting door, and a count would have to disclose rows to
 * count them — so a full page reports `total: null` ("more may follow"), which
 * `GridPager` renders as an open-ended range.
 */
async function subjectPage<T>(t: Transport, entity: string, opts: SelectOptions | undefined): Promise<Page<T>> {
  const rows = await subjectList<T>(t, entity, opts)
  const limit = opts?.limit ?? 0
  const offset = opts?.offset ?? 0
  return { rows, total: rows.length < limit ? offset + rows.length : null }
}

/** `core.subject_get_many`, one POST per batch of at most 200 distinct ids. */
async function subjectGetMany<T>(t: Transport, entity: string, ids: readonly string[]): Promise<T[]> {
  const wanted = [...new Set(ids.filter((id) => id != null && id !== ''))]
  const out: T[] = []
  for (let i = 0; i < wanted.length; i += SUBJECT_MAX_ROWS) {
    const rows = await t.rpcTable<T>('subject_get_many',
      { p_entity: entity, p_ids: wanted.slice(i, i + SUBJECT_MAX_ROWS) }, 'core')
    if (Array.isArray(rows)) out.push(...rows)
  }
  return out
}

/** The persona/deployment-resolved presentation identity of one surfaced entity. */
export interface EntityIdentityInfo {
  entity_type: string
  module: string
  singular_label: string
  plural_label: string
  icon_key: string | null
  /** Whether record labels can be requested from the caller-RLS `titles_of` door. */
  title_declared: boolean
  /**
   * WHY this surface is not a navigable record, or null when it is one (#1993).
   *
   * A ledger, a join row, an append-only evidence table and a reference
   * vocabulary all legitimately carry a view without having a record to open.
   * Before this, that was indistinguishable from a browsable entity nobody had
   * given a title — which is how 249 of the 283 surfaced entities came to lack
   * one unnoticed. A host reads it to SUPPRESS record navigation rather than
   * inferring "not a record" from "no title", which are different facts.
   *
   * It carries the sentence rather than a boolean on purpose: a flag can be set
   * to true with no argument, and "the author did not say" is the condition
   * that produced the gap.
   */
  non_record_surface?: string | null
  /** One governed answer used by every generic record-open seam. */
  navigation_eligible: boolean
}
/**
 * A registered relationship type (core 0001/0002/0025) — the metamodel's answer
 * to "what may be linked to what".
 *
 * `storage` decides where the link LIVES, and therefore how it is written:
 *   · `edge` — a row in `core.relationship` (many-to-many, attributed, temporal);
 *   · `fk`   — `fk_column` ON `from_type`'s TABLE, pointing at the row a
 *     DECLARED ENDPOINT PAIR names. Not every fk has one canonical target: a
 *     discriminated fk may be TARGET-NEUTRAL, and then `relationship_pairs` is
 *     the only statement of what the column may point at (see `to_type`).
 *
 * `label` reads from `from_type`'s point of view; `inverse_label` (core
 * 2026080215) reads from the target's, so a surface showing an ORGANIZATION's
 * people can say "People" where the person's own page says "Works at". It is
 * nullable: consumers fall back mechanically rather than refusing to render.
 */
export interface RelationshipTypeInfo {
  key: string
  module: string
  label: string
  inverse_label?: string | null
  from_type: string
  /**
   * The canonical target — or NULL for a TARGET-NEUTRAL declaration (core
   * 0.256.0, #1455 / ADR F-33.1).
   *
   * A module that owns a polymorphic pointer usually cannot name any of its
   * targets: `approvals` and `documents` both declare `dependsOn: [core]`, so
   * `cpq_quote` and `billing_invoice` do not exist when they install. Such a
   * type declares NO canonical target and every module that introduces a real
   * one contributes an explicit `relationship_pairs` row instead.
   *
   * Null is legal ONLY for `storage: 'fk'` with both `fk_column` and
   * `discriminator_column` set; the database refuses it for an edge or a
   * monomorphic fk. **A host must never render or key on this value without
   * checking it** — the pairs are the authority, and a type with a null target
   * and no pairs has no endpoint at all and must contribute no slot.
   */
  to_type: string | null
  cardinality: 'one_to_one' | 'one_to_many' | 'many_to_one' | 'many_to_many'
  storage: 'edge' | 'fk'
  fk_column?: string | null
  /**
   * For `storage: 'fk'`: the sibling column on `from_type`'s table that says
   * WHICH registered target a given row points at — `documents.document`'s
   * `subject_type` beside its `subject_id` (core 0.249.0, #1435).
   *
   * When it is set, the type may declare more than one endpoint pair and each
   * pair's `discriminator_value` (defaulting to its own `to_type`) selects the
   * rows that belong to it. A host reading or writing an fk link MUST carry the
   * predicate: without it the read is correct only for as long as ids happen
   * not to collide across the target tables, and nothing enforces that — a
   * polymorphic pointer has no foreign key. Null ⇒ the fk is monomorphic.
   *
   * It is also what makes a NULL `to_type` legal (core 0.256.0): a
   * target-neutral declaration is always discriminated, because a column that
   * can point at several tables has to say which one each row means.
   */
  discriminator_column?: string | null
  directed?: boolean
  is_identity?: boolean
  /**
   * The facts this LINK carries, resolved by `core.relationship_attributes()`.
   *
   * This is deliberately the same small vocabulary as `field_meta`: a host can
   * render an attributed edge without inventing relationship-specific columns,
   * and an option catalogue is already resolved on the server. It is optional
   * for compatibility with deployments before core 2026080302.
   */
  attributes?: Record<string, RelationshipAttributeInfo>
  /** Stored write classification, with handler details deliberately withheld. */
  write_mode?: 'generic' | 'governed'
  /** The closed operations the universal relationship host may offer. */
  write_operations?: ('create' | 'link' | 'update' | 'end')[]
}

/** One declared field on a relationship edge. */
export interface RelationshipAttributeInfo {
  label?: string
  kind?: string
  sort?: number
  /**
   * The resolved vocabulary. `selectable: false` means the value is present and
   * labelled but closed to a NEW choice — an archived definition keeps its
   * label on the edges that already carry it (#1075).
   */
  options?: { value: unknown; label: string; selectable?: boolean }[]
  options_source?: string
}

/** An additional endpoint pair a polymorphic relationship type accepts (core 0025). */
export interface RelationshipPairInfo {
  rel_type: string
  from_type: string
  to_type: string
  /**
   * The token in the type's `discriminator_column` that selects THIS pair.
   * Null ⇒ the pair's own `to_type`, which is the case for every polymorphic
   * pointer in the tree (the stored token IS the entity key). Present only on
   * a deployment at core 0.249.0 or later.
   */
  discriminator_value?: string | null
}

export interface SchemaDescription {
  entities: EntityTypeInfo[]
  relationships: RelationshipTypeInfo[]
  /** absent on a pre-0025 deployment — treat as [] */
  relationship_pairs?: RelationshipPairInfo[]
}
export interface Neighbour { rel_type: string; node_type: string; node_id: string; depth: number }

/** The semantic layer (0019) — meaning + presentation + reporting. */
export interface SemanticField {
  entity_type: string; column_name: string; label: string; kind: string
  format?: string | null; unit?: string | null; options: unknown[]
  sort: number; is_summary: boolean; classification: string; term_key?: string | null
  required?: boolean; read_only?: boolean; section?: string | null
  rules?: Record<string, unknown>
  /**
   * Does the DATABASE fill this column when the statement omits it — a column
   * DEFAULT, an identity, or a generated expression (core 2026083011, #1579)?
   *
   * The BOOLEAN only: the default expression is server business and is
   * deliberately not exposed. It is what lets a create form tell "the user must
   * supply this" apart from "this is NOT NULL and the server already knows it",
   * which `required` alone cannot say. Optional because a deployment on an older
   * core answers without it, and absent must read as "no, do not assume".
   */
  has_server_default?: boolean
}
export interface Measure {
  key: string; module?: string | null; entity_type?: string | null; label: string
  aggregation: string; column_name?: string | null; filter?: string | null; format?: string | null
}
export interface GlossaryTerm { key: string; label: string; definition: string; synonyms: string[]; module?: string | null }
export interface SemanticDescription { fields: SemanticField[]; measures: Measure[]; glossary: GlossaryTerm[] }

/**
 * A related LIST declared by a form section, naming a registered relationship
 * and which end this form stands on (core `2026080301`).
 *
 * `core.lint_experience` rule 6 refuses a bare string, an unregistered
 * `rel_type`, or a direction other than in/out — so anything that reaches a
 * client has already been checked against `core.relationship_type`. The type
 * is stated here because it was NOT: the grammar has carried `related` since
 * `core/0030:93` (`[{title, description?, fields?[], related?}]`) and this
 * interface described only two of the four keys.
 */
export interface RelatedSectionSpec {
  rel_type: string
  direction: 'in' | 'out'
}

/** An experience-layer L1 document (0021) — declarative screen layout. */
export interface ExperienceDocument {
  columns?: string[]
  /**
   * `fields` is OPTIONAL, matching the column comment on `core.form.sections`.
   * A related-only section — the exact example `2026080301` documents,
   * `{"title": "People", "related": {…}}` — carries no `fields` at all, and a
   * type that required one is why `buildForm` threw `Cannot read properties of
   * undefined (reading 'map')` on it rather than rendering the section.
   */
  sections?: { title: string; description?: string; fields?: string[]; related?: RelatedSectionSpec }[]
  measures?: string[]
}
/** The kinds core.experience actually admits, as of #1188.
 *  This union was `'view' | 'form' | 'dashboard' | 'app'` and was wrong in both
 *  directions: it had never gained 'nav', 'panels' or 'related' as those kinds
 *  shipped, and it still advertised 'dashboard', which 2026090512 removes from
 *  experience_kind_check — a dashboard is now core.dashboard, not a document.
 *  Widening is safe: the type appears only in parameter position
 *  (getExperience below, and apps/console useExperience), never in a switch.
 *
 *  Gains 'table' with core 0.338.0 (#1982): a table-presentation declaration
 *  is a core.experience row, so it is in experience_kind_check and belongs
 *  here. Omitting it would be the same drift this comment already records. */
export type ExperienceKind = 'view' | 'form' | 'app' | 'nav' | 'panels' | 'related' | 'table' | 'page'

/** An MCP tool registered as data (core 0033) — a module's agent WORKFLOW verb.
 *  `bind` is the connector's execution spec (rpc / entity / builtin). */
export interface McpToolSpec {
  name: string
  description: string
  module: string
  mutates: boolean
  inputSchema: Record<string, unknown>
  bind: Record<string, unknown>
  /** Effective caller-scoped policy. Proposal-only until a governed human act confirms it. */
  requiresConfirmation?: boolean
  /** The effective policy requires a decision by an authorized approver. */
  requiresApproval?: boolean
  /** Monotone policy result for this surface. */
  policyEffect?: 'allow' | 'confirm' | 'approval' | 'deny'
  /** Whether an explicit layered policy row applied (absence retains the declared default). */
  policyExplicit?: boolean
  /**
   * Whether this tool is an ADMINISTRATION door — the tool half of the governed
   * classification described on `EntityTypeInfo.palette_class`.
   *
   * It is carried separately from the entity because the two questions differ:
   * `approvals.search_templates` searches a process DEFINITION while
   * `approvals.search_approvals` searches the work, and both belong to the same
   * module. A door is administrative when what it searches is configuration,
   * whatever the module.
   *
   * Optional for the same reason and with the same consequence: `core.mcp_tool`
   * has no such column yet, so this is `undefined` on every deployment today.
   */
  paletteClass?: 'business' | 'administrative' | null
}

/**
 * One governed act a record surface may offer, as `core.my_entity_actions`
 * returns it (#1131, ruling 40821).
 *
 * The identity argument is NAMED rather than inferred — that is the whole point
 * of the binding. A client that guessed which input carries the record id would
 * send the verb something it ignores, and the act would run against nothing
 * while the surface reported success.
 */
export interface EntityAction {
  /** The registered MCP tool this launches. It stays the invocation contract. */
  tool: string
  label: string
  description: string
  module: string
  mutates: boolean
  /** `row` needs one record, `batch` a selection, `none` neither. */
  cardinality: 'row' | 'batch' | 'none'
  /** The tool input that carries the record id. Null when `cardinality: 'none'`. */
  idArgument: string | null
  /** The tool input that carries the selected ids, for a `batch` binding. */
  idsArgument: string | null
  /** `form` renders the shared schema form; `route` opens the module's own desk. */
  launch: 'form' | 'route'
  route: string | null
  inputSchema: Record<string, unknown>
  requiresConfirmation: boolean
  /**
   * May this caller take the act on THIS record — answered by `core.can_act`,
   * never inferred here. Null when no record was named, because a list has no
   * row to ask about and `false` there would read as a refusal.
   */
  canAct: boolean | null
}

/** What a SELECTION may take, as `core.my_entity_action_capability` answers it. */
export interface EntityActionCapability {
  /** `all` · `some` · `none` — never a boolean, because `some` is a real answer. */
  verdict: 'all' | 'some' | 'none'
  total: number
  allowed: number
  /** WHICH ids pass, so a surface can act on exactly those rather than guess. */
  allowedIds?: string[]
}

export interface AiToolManifest {
  tools: McpToolSpec[]
  manifestHash: string
  policyApplied: {
    toolName: string
    enabled: boolean
    requiresConfirmation: boolean
    requiresApproval?: boolean
    effect?: 'allow' | 'confirm' | 'approval' | 'deny'
    explicit?: boolean
  }[]
  omissions: { name: string; reason: string }[]
  budget: number
}

/** A mutation's server-bound confirmation/approval state. Arguments are never
 * returned or persisted; `proposalId` binds their canonical hash. */
export interface McpActionProposal {
  ready: boolean
  proposalId?: string
  effect?: 'allow' | 'confirm' | 'approval'
  status?: 'awaiting_confirmation' | 'awaiting_approval' | 'executing' | 'executed'
    | 'rejected' | 'cancelled' | 'expired' | 'stale' | 'failed' | 'refused' | 'withheld'
  expiresAt?: string
  approvalRequestIds?: string[]
  message?: string
  preview?: {
    operation: 'create' | 'update' | 'delete' | 'action'
    entity: string
    targetIds: string[]
    changedFields: string[]
    changes: {
      field: string
      old: { state: 'absent' } | { state: 'value'; value: unknown }
      new: { state: 'absent' } | { state: 'value'; value: unknown }
    }[]
    changeCount: number
    changesTruncated: boolean
  }
  failureCode?: string
  replay?: boolean
  result?: {
    outcome: 'ok'
    operation: 'create' | 'update' | 'delete' | 'action'
    entity: string
    targetIds: string[]
    affected: number
  }
}

export interface AiInstructionItem {
  logicalKey: string
  scope: string
  revisionId: string
  revision: number
  kind: string
  mergeStrategy: string
  applicability: Record<string, unknown>
  content: string
  contentHash: string
}

export interface AiInstructionBundle {
  policy: AiInstructionItem[]
  steering: AiInstructionItem[]
  work: unknown[]
  rules: unknown[]
  tools: unknown[]
  meta: {
    locale: string
    instructionBundleHash: string
    revisions: { logicalKey: string; revisionId: string; revision: number; contentHash: string }[]
    omissions: { key?: string; reason: string }[]
  }
}

/**
 * One caller-scoped provider's answer about the record in hand (core 0.298.0,
 * #1397 slice 2). `trust` is always `untrusted_data` and the shape enforces it:
 * `facts` is whatever the module's provider returned, wrapped rather than
 * merged, so nothing inside it can reach the instruction channel.
 */
export interface AiWorkContextItem {
  capability: 'record' | 'task' | 'approval' | 'action'
  module: string
  provider: string
  entity: string | null
  recordId: string
  trust: 'untrusted_data'
  facts: unknown
}

/** A rule DECLARATION scoped to the record's module — never a verdict (core 0.298.0). */
export interface AiRuleDeclaration {
  module: string
  code: string
  label: string | null
  trigger: string
  severity: 'info' | 'warning' | 'error'
  actionType: string
  message: string | null
  condition: Record<string, unknown>
  trust: 'governed_declaration'
}

/** The whole compiled envelope: trusted instructions, untrusted record facts, rules, tools. */
export interface AiContextBundle extends Omit<AiInstructionBundle, 'work' | 'rules' | 'tools' | 'meta'> {
  work: AiWorkContextItem[]
  rules: AiRuleDeclaration[]
  tools: McpToolSpec[]
  meta: AiInstructionBundle['meta'] & {
    surface: string | null
    entity: string | null
    recordId: string | null
    workContextHash: string
    workProvenance: { provider: string; capability: string; recordId: string; factsHash: string }[]
    rulePackHash: string
    toolManifestHash: string
    toolPolicyApplied: AiToolManifest['policyApplied']
    contextBundleHash: string
    workBudget: number
    toolBudget: number
  }
}

/** What a screen is about (core 0.301.0). `registered` distinguishes a real
 *  mapping from the identity fallback, so a caller can tell the two apart. */
export interface AiSurface {
  kind: string
  module: string
  entity: string | null
  label: string | null
  registered: boolean
}

/** A declared setting + its current value (core 0034). Secret values arrive masked. */
export interface SettingRow {
  module: string
  key: string
  label: string
  kind: 'text' | 'number' | 'boolean' | 'secret' | 'select' | 'json'
  options?: { value: string; label: string }[] | null
  is_secret: boolean
  description?: string | null
  sort: number
  is_set: boolean
  value: unknown
}

export type RelationshipBehaviourAxis =
  | 'kind'
  | 'write_requires'
  | 'on_parent_delete'
  | 'reparent'

export interface RelationshipBehaviourOption {
  key: string
  label: string
  sort: number
}

/** One effective parent-edge policy beside the module preset it overrides. */
export interface RelationshipBehaviourRow {
  child_entity_type: string
  child_label: string
  fk_column: string
  parent_entity_type: string
  parent_label: string
  kind: string
  write_requires: string
  on_parent_delete: string
  reparent: string
  system_kind: string
  system_write_requires: string
  system_on_parent_delete: string
  system_reparent: string
  is_overridden: boolean
  override_reason?: string | null
  overridden_at?: string | null
}

/**
 * A party role type's availability (#1075).
 *
 * `archived` means the definition may not be NEWLY assigned. It does not mean
 * it is gone: every existing assignment keeps this row's real label, and the
 * pickers render it disabled with an Archived marker rather than blank.
 */
export type PartyRoleTypeStatus = 'active' | 'archived'

/** One party role definition beside the usage that would survive archiving it. */
export interface PartyRoleTypeRow {
  key: string
  label: string
  module: string
  is_commercial: boolean
  applies_to: 'person' | 'organization' | 'any'
  tone?: string | null
  sort: number
  status: PartyRoleTypeStatus
  /** Assignments carrying this role, in any state. */
  in_use: number
  /** Assignments carrying this role and currently active. */
  active_in_use: number
}

export interface RelationshipBehaviourDescription {
  options: Record<RelationshipBehaviourAxis, RelationshipBehaviourOption[]>
  relationships: RelationshipBehaviourRow[]
}

/**
 * The privilege context a client's TRANSPORT authenticates as — the one fact the
 * SDK cannot observe (a `Transport` is opaque) and therefore must be told.
 *
 *   · `api` (default) — an API-population session (`authenticated`/`anon`).
 *     `core.enforce_write_doors()` has revoked direct DML on `rpc`-door entities
 *     from exactly this population, so `EntityHandle` refuses such a write
 *     LOCALLY, before any transport call: the server's answer is already known
 *     (`403 permission denied for table …`, for everybody), and a client that
 *     dispatches anyway is a client asserting a write that cannot land.
 *   · `service` — a principal that is past the door BY CONSTRUCTION: the table
 *     owner, `service_role`, a no-session service context (an ETL, the
 *     scheduler). The revoke never applied to it, so the handle dispatches and
 *     the SERVER stays the authority — this flag never widens what the server
 *     accepts, it only stops the client refusing what the server would take.
 */
export type ClientPrivilege = 'api' | 'service'

export interface ClientOptions {
  /** See {@link ClientPrivilege}. Default `'api'` — the refusing, honest one. */
  privilege?: ClientPrivilege
}

/**
 * The local refusal for a shut write door (#1131). Modelled on the estate's
 * refusal style (catalog/0016): it says what the door IS, never what the caller
 * lacks — an `rpc` door is not an authorization near-miss a different grant
 * would fix, it is the declaration that this entity is changed through the
 * module's own governed actions.
 */
export class WriteDoorClosedError extends Error {
  /** Stable machine marker — survives bundling/duplication where `instanceof` does not. */
  readonly code = 'write_door_closed'
  constructor(readonly entity: string, readonly verb: 'create' | 'update' | 'delete') {
    super(
      `'${entity}' is changed through its governed actions — its registered write door is 'rpc', `
      + `so a direct ${verb} is not a route to it and nothing was sent. `
      + `Use the module's own action (its RPC, or the MCP tool that wraps it) to make this change.`,
    )
    this.name = 'WriteDoorClosedError'
  }
}

/** Structural check for the refusal above — works across package copies. */
export function isWriteDoorClosed(e: unknown): e is WriteDoorClosedError {
  return typeof e === 'object' && e !== null && (e as { code?: unknown }).code === 'write_door_closed'
}

/** How a write to a subject table asks for its result — the handle only (#2444). */
const SUBJECT_WRITE: WriteOptions = { returning: 'id' }

/** A handle to one registered entity — CRUD flows through RLS as the caller. */
export class EntityHandle {
  constructor(private t: Transport, readonly info: EntityTypeInfo, private privilege: ClientPrivilege = 'api') {}
  /**
   * Refuse BEFORE the transport where the registry says direct DML is not a
   * route (#1131). Fires ONLY on an explicit `'rpc'` — an absent/undeclared
   * door stays `direct` (see `EntityTypeInfo.write_door`: refusing on absence
   * would turn every pre-0.151 deployment read-only). A `service`-privilege
   * client skips the check: the revoke that shut the door names the API
   * population, not the owner/service principals an ETL writes as — and the
   * server remains the authority either way.
   */
  private assertDoorOpen(verb: 'create' | 'update' | 'delete'): void {
    if (this.privilege === 'service') return
    if (this.info.write_door !== 'rpc') return
    throw new WriteDoorClosedError(this.info.key, verb)
  }
  /** Is this entity read only through the governed subject doors (#2444)? */
  get subject(): boolean {
    return isSubjectEntity(this.info)
  }
  /**
   * Rows of this entity. A `subject` entity is read through
   * `core.subject_list` — one POST per page, in the door's closed vocabulary
   * (`subject.ts`); anything it cannot express REJECTS with
   * `SubjectReadUnsupportedError` and nothing is sent. Every other entity is
   * the ordinary RLS read.
   */
  // async so a vocabulary refusal is a REJECTION, like the write-door one.
  async list<T = Row>(opts?: SelectOptions): Promise<T[]> {
    if (this.subject) return subjectList<T>(this.t, this.info.key, opts)
    return this.t.select<T>(this.info.schema_name, this.info.table_name, opts)
  }
  /** `list` plus the total that matches — null for a subject page that came back full. */
  async listPage<T = Row>(opts?: SelectOptions): Promise<Page<T>> {
    if (this.subject) return subjectPage<T>(this.t, this.info.key, opts)
    return this.t.selectPage<T>(this.info.schema_name, this.info.table_name, opts)
  }
  /** One row by id, or null when absent or not visible (indistinguishable, by design). */
  async get<T = Row>(id: string): Promise<T | null> {
    if (this.subject) {
      const row = await this.t.rpcScalar<T | null>('subject_get', { p_entity: this.info.key, p_id: id }, 'core')
      return row ?? null
    }
    return (await this.t.select<T>(this.info.schema_name, this.info.table_name, { where: { id }, limit: 1 }))[0] ?? null
  }
  /**
   * The rows among `ids` this caller may see, ordered by id on a subject
   * entity — never the ids that were asked for: a requested row the caller
   * cannot see is simply absent (and, on a subject entity, leaves no trace in
   * the audit chain). One request per batch: 200 ids through the subject door,
   * 100 through the URL of an ordinary read.
   */
  async getMany<T = Row>(ids: readonly string[]): Promise<T[]> {
    if (this.subject) return subjectGetMany<T>(this.t, this.info.key, ids)
    const wanted = [...new Set(ids.filter((id) => id != null && id !== ''))]
    const out: T[] = []
    for (let i = 0; i < wanted.length; i += 100) {
      const chunk = wanted.slice(i, i + 100)
      out.push(...await this.t.select<T>(this.info.schema_name, this.info.table_name, {
        limit: chunk.length,
        filters: [{ key: 'id', value: `in.(${chunk.map((x) => `"${x}"`).join(',')})` }],
      }))
    }
    return out
  }
  // async so the door refusal is a REJECTION, not a sync throw — every failure
  // of a promise-returning verb arrives the same way, and a caller chaining
  // .catch() without await is not crashed by the one failure that is local.
  async create<T = Row>(row: Row): Promise<T> {
    this.assertDoorOpen('create')
    if (!this.subject) return this.t.insert<T>(this.info.schema_name, this.info.table_name, row)
    return this.readBack<T>(await this.t.insert<{ id?: unknown }>(
      this.info.schema_name, this.info.table_name, row, SUBJECT_WRITE))
  }
  async update<T = Row>(id: string, patch: Row): Promise<T> {
    this.assertDoorOpen('update')
    if (!this.subject) return this.t.update<T>(this.info.schema_name, this.info.table_name, id, patch)
    return this.readBack<T>(await this.t.update<{ id?: unknown }>(
      this.info.schema_name, this.info.table_name, id, patch, SUBJECT_WRITE))
  }
  /**
   * A subject write returns the row handle only (`select=id`); the row itself
   * is read THROUGH the door, so the person who just saved it is receipted as
   * having seen it — exactly like anyone else who opens it. Where the door
   * does not return it (the caller wrote a row their own RLS will not show
   * them), the write still happened: the handle is what they get.
   */
  private async readBack<T>(written: { id?: unknown } | null | undefined): Promise<T> {
    const id = written?.id
    if (typeof id !== 'string' || id === '') return (written ?? {}) as T
    return ((await this.get<T>(id)) ?? { id }) as T
  }
  async delete(id: string): Promise<void> {
    this.assertDoorOpen('delete')
    return this.t.delete(this.info.schema_name, this.info.table_name, id)
  }
}

/** An entitled module's surface: its registered entities, resolved from the metamodel. */
export class ModuleHandle {
  constructor(private t: Transport, readonly name: string, readonly entities: EntityTypeInfo[], private privilege: ClientPrivilege = 'api') {}
  entity(key: string): EntityHandle | null {
    const info = this.entities.find((e) => e.key === key)
    return info ? new EntityHandle(this.t, info, this.privilege) : null
  }
}

/**
 * What one registered entity says about direct DML on ITS table, keyed by the
 * `schema.table` a table-addressed write names (#1131).
 *
 * `EntityHandle` never needs this — it is constructed FROM the entity row, so
 * the door is already in its hand. The raw transport is the case the door could
 * not reach even in principle: `client.raw().update('cpq', 'quote_line', …)`
 * names a schema and a table and no entity at all, so the fact it must respect
 * is not among its arguments and has to be looked up.
 */
interface WriteDoorFact {
  /** The entity key, so a refusal names the thing the person sees, not a table. */
  key: string
  door: 'direct' | 'rpc'
  /** The table is read only through the subject doors (#2444). */
  subject: boolean
}

export class SolumClient {
  constructor(private t: Transport, private opts: ClientOptions = {}, shared?: { doors: Map<string, WriteDoorFact> }) {
    this.doors = shared?.doors ?? new Map<string, WriteDoorFact>()
  }

  /**
   * This client, with `x-solum-correlation-id: <id>` on every request it makes
   * (#2444). One tool call runs on one of these, so the invocation it records
   * and every audit row and receipt its work produces resolve the same id
   * server-side — the join from "the agent called X" to "X changed these".
   *
   * The id is a JOIN KEY, not a credential: it authenticates nothing and the
   * server resolves identity from the token exactly as before. The sibling
   * SHARES this client's learned write doors (#1131), so a correlated call is
   * refused a raw write exactly as the parent would be. A transport that
   * cannot scope headers yields an uncorrelated sibling, never an error.
   */
  withCorrelation(correlationId: string): SolumClient {
    const scoped = this.t.withHeaders?.({ 'x-solum-correlation-id': correlationId }) ?? this.t
    return new SolumClient(scoped, this.opts, { doors: this.doors })
  }

  /**
   * `schema.table` -> what its registering entity says about direct DML.
   *
   * LEARNED, never configured (rule 42): every read of the registry this client
   * already makes feeds it — `describeSchema`, `module`, and the single-row
   * `entity` lookup — so the map is DERIVED from the deployment in front of it
   * rather than restated in each host, where it would be a second list to keep
   * true. A host that already holds a `SchemaDescription` can hand it over
   * directly with `learnWriteDoors`.
   */
  private readonly doors: Map<string, WriteDoorFact>

  /** Memoised so `raw() === raw()`: hosts hold on to the transport they got. */
  private guardedRaw: Transport | null = null

  /**
   * Record what these entity rows say about their own tables' write doors.
   *
   * MERGES per table rather than replacing the map: one entity row is
   * authoritative for its own table and says nothing whatsoever about any
   * other, so a single-entity read and a whole-schema read can both feed this
   * without either erasing what the other learned. A later read of the SAME
   * table wins, which is how a door that flips stops being stale.
   */
  learnWriteDoors(entities: readonly EntityTypeInfo[] | undefined | null): void {
    for (const e of entities ?? []) {
      if (!e || !e.schema_name || !e.table_name || !e.key) continue
      this.doors.set(e.schema_name + '.' + e.table_name, {
        key: e.key,
        // Mirrors `EntityHandle.assertDoorOpen` EXACTLY: only an explicit 'rpc'
        // is a shut door. Anything else — 'direct', absent, an unknown future
        // value — resolves to 'direct', because refusing on absence would turn
        // every deployment older than the column read-only.
        door: e.write_door === 'rpc' ? 'rpc' : 'direct',
        // Only an explicit 'subject' routes a read through the doors. Absent
        // (an older deployment) is `request`: nothing there closed a table.
        subject: e.read_audit === 'subject',
      })
    }
  }

  /**
   * The refusal `EntityHandle` makes, for the write that names a TABLE (#1131).
   *
   * Three properties this deliberately keeps identical to the entity path, so
   * the two can never drift into disagreeing about what a door means:
   *
   *   · only an explicit `rpc` refuses;
   *   · a `service`-privilege client is past the door by construction and is
   *     never refused locally — the revoke named the API population, not the
   *     table owner an ETL writes as;
   *   · the error is the same `WriteDoorClosedError`, so one `isWriteDoorClosed`
   *     check in a host covers both routes.
   *
   * An UNKNOWN table dispatches. That is not a hole, and it is worth being
   * exact about why: the server's revoke is the enforcement and stays the
   * authority either way — this guard exists so the client stops asserting a
   * write whose answer is already known, which is HONESTY, not authorization.
   * Refusing an unknown table would instead refuse every write to a table no
   * entity registers (`core.access_grant`, `core.user_view_pref`,
   * `core.relationship`, `core.rule` — all live console writes today) and every
   * write issued before the first registry read resolves.
   */
  private assertRawDoorOpen(verb: 'create' | 'update' | 'delete', schema: string, table: string): void {
    if ((this.opts.privilege ?? 'api') === 'service') return
    const fact = this.doors.get(schema + '.' + table)
    if (!fact || fact.door !== 'rpc') return
    throw new WriteDoorClosedError(fact.key, verb)
  }

  /**
   * The transport, with the learned write doors applied to the three
   * table-addressed write verbs.
   *
   * `raw()` was the escape hatch the door could not reach: #1131 measured
   * fourteen non-test console writes addressed by `schema` + `table` strings —
   * `cpq.quote_line`, `catalog.price`, `comms.post`, `engagements.action` and
   * `approvals.process_definition` among them — none of which consults a door,
   * because `EntityHandle` is not on their path. Guarding HERE makes the door a
   * property of the WRITE rather than of the SURFACE: every present and future
   * caller is covered by construction, instead of each one being audited.
   *
   * READS of a learned SUBJECT table (#2444 slice 4) are routed through the
   * governed doors, for the same reason: `raw().select('crm', 'lead', …)`
   * names a table, not an entity, and every such call site — present or future
   * — is covered by construction rather than by audit. The translation is the
   * one `EntityHandle.list` uses, so it refuses exactly what that refuses; a
   * write to such a table asks for the row handle only. Any other table's read
   * passes through untouched, and an UNKNOWN table (no registry read yet)
   * dispatches directly — the server's revoke answers that one.
   */
  raw(): Transport {
    if (this.guardedRaw) return this.guardedRaw
    const t = this.t
    const guard = (verb: 'create' | 'update' | 'delete', schema: string, table: string) =>
      this.assertRawDoorOpen(verb, schema, table)
    const subjectOf = (schema: string, table: string): string | null => {
      const fact = this.doors.get(schema + '.' + table)
      return fact?.subject ? fact.key : null
    }
    this.guardedRaw = {
      rpcScalar<T = unknown>(fn: string, args?: Row, schema?: string): Promise<T> {
        return t.rpcScalar<T>(fn, args, schema)
      },
      rpcTable<T = Row>(fn: string, args?: Row, schema?: string): Promise<T[]> {
        return t.rpcTable<T>(fn, args, schema)
      },
      async select<T = Row>(schema: string, table: string, opts?: SelectOptions): Promise<T[]> {
        const subject = subjectOf(schema, table)
        if (subject) return subjectList<T>(t, subject, opts)
        return t.select<T>(schema, table, opts)
      },
      async selectPage<T = Row>(schema: string, table: string, opts?: SelectOptions): Promise<Page<T>> {
        const subject = subjectOf(schema, table)
        if (subject) return subjectPage<T>(t, subject, opts)
        return t.selectPage<T>(schema, table, opts)
      },
      // async, so the refusal is a REJECTION and not a synchronous throw — the
      // reason `EntityHandle`'s verbs are async too: a caller chaining .catch()
      // without await must not be crashed by the one failure that is local.
      async insert<T = Row>(schema: string, table: string, row: Row, opts?: WriteOptions): Promise<T> {
        guard('create', schema, table)
        return t.insert<T>(schema, table, row, subjectOf(schema, table) ? { ...opts, returning: 'id' } : opts)
      },
      async update<T = Row>(schema: string, table: string, id: string, patch: Row, opts?: WriteOptions): Promise<T> {
        guard('update', schema, table)
        return t.update<T>(schema, table, id, patch, subjectOf(schema, table) ? { ...opts, returning: 'id' } : opts)
      },
      async delete(schema: string, table: string, key: string | Row): Promise<void> {
        guard('delete', schema, table)
        return t.delete(schema, table, key)
      },
    }
    return this.guardedRaw
  }

  /** The metamodel — entities + relationships. The source for typegen + UX. */
  async describeSchema(): Promise<SchemaDescription> {
    const desc = await this.t.rpcScalar<SchemaDescription>('describe_schema')
    this.learnWriteDoors(desc?.entities)
    return desc
  }

  /** All surfaced entity identities in one persona-aware round trip (#1705). */
  describeEntityIdentities(persona?: string): Promise<EntityIdentityInfo[]> {
    return this.t.rpcTable<EntityIdentityInfo>(
      'describe_entity_identities', { p_persona: persona ?? null }, 'core')
  }

  /** Typed-edge graph traversal from a node. `direction` (0025): 'out' follows
   *  edges FROM the node, 'in' follows edges INTO it (e.g. "who works at this
   *  org?"), 'both' either way. */
  neighbours(type: string, id: string, relTypes?: string[], depth = 1, direction: 'out' | 'in' | 'both' = 'out'): Promise<Neighbour[]> {
    return this.t.rpcTable<Neighbour>('get_neighbours', {
      p_type: type, p_id: id, p_rel_types: relTypes ?? null, p_depth: depth, p_direction: direction,
    })
  }

  /** The semantic layer — field dictionary + measures + glossary (optionally for one entity).
   *  Structure's companion: the experience renderer + agents read both. */
  describeSemantics(entityType?: string): Promise<SemanticDescription> {
    return this.t.rpcScalar<SemanticDescription>('describe_semantics', { p_entity_type: entityType ?? null })
  }

  /** MCP tools-as-data (core 0033) — the module-registered agent verbs the
   *  license entitles, filtered server-side. The MCP connector composes these
   *  with its generic metamodel tools; adding one is a module migration, not a
   *  connector edit. */
  describeTools(): Promise<McpToolSpec[]> {
    return this.t.rpcScalar<McpToolSpec[]>('describe_tools')
  }

  /**
   * The governed acts a record surface may OFFER this caller (#1131).
   *
   * The server answers, and the surface renders what it is told: `canAct` comes
   * from `core.can_act` rather than from a persona, a role name or a status
   * string compared in React. Pass `id` for a record, or omit it on a list —
   * with no record in hand `canAct` is null, because answering `false` there
   * would read as a refusal and hide a legal batch action.
   *
   * Hiding is UX honesty, never authorization: the verb still refuses on its own.
   */
  myEntityActions(entity: string, id?: string): Promise<EntityAction[]> {
    return this.t.rpcScalar<EntityAction[]>('my_entity_actions', { p_entity: entity, p_id: id ?? null })
  }

  /**
   * May this caller take one bound act on EACH of these records?
   *
   * A selection has three honest answers, not two. Collapsing `some` to "no"
   * hides work the caller may legitimately do; collapsing it to "yes" produces
   * a partial run the person reads as complete, which is worse because nothing
   * on screen says otherwise.
   *
   * UX honesty, never authority — the verb refuses on its own for every id.
   */
  myEntityActionCapability(entity: string, tool: string, ids: string[]): Promise<EntityActionCapability> {
    return this.t.rpcScalar<EntityActionCapability>('my_entity_action_capability', {
      p_entity: entity, p_tool: tool, p_ids: ids,
    })
  }

  /** One caller-scoped, policy-resolved, canonically hashed manifest shared by MCP and copilot. */
  resolveAiToolManifest(
    preferredModules: string[] = [],
    budget = 48,
    supportedBindKinds: string[] = ['rpc', 'entity', 'builtin'],
    surface?: 'all' | 'mcp' | 'copilot' | 'api' | 'web' | 'extension',
  ): Promise<AiToolManifest> {
    return this.t.rpcScalar<AiToolManifest>(surface ? 'resolve_ai_tool_manifest_v2' : 'resolve_ai_tool_manifest', {
      p_preferred_modules: preferredModules,
      p_budget: budget,
      p_supported_bind_kinds: supportedBindKinds,
      ...(surface ? { p_surface: surface } : {}),
    })
  }

  /** One server-derived, monotone policy verdict for an exact attempted act. */
  resolveAiToolPolicy(opts: {
    tool: string
    surface?: 'all' | 'mcp' | 'copilot' | 'api' | 'web' | 'extension'
    module?: string
    entity?: string
    action?: string
    mutates?: boolean
  }): Promise<{
    effect: 'allow' | 'confirm' | 'approval' | 'deny'
    allowed: boolean
    requiresConfirmation: boolean
    requiresApproval: boolean
    policyExplicit: boolean
    module: string | null
    evidence: Record<string, unknown>[]
    approvalKinds: string[]
    approvalReducedToConfirmation?: boolean
  }> {
    return this.t.rpcScalar('resolve_ai_tool_policy', {
      p_tool_name: opts.tool,
      p_surface: opts.surface ?? 'all',
      p_module: opts.module ?? null,
      p_entity: opts.entity ?? null,
      p_action: opts.action ?? null,
      p_mutates: opts.mutates ?? false,
    }, 'core')
  }

  /** Execute or advance a protected mutation in one database transaction. */
  executeMcpAction(opts: {
    proposalId?: string
    tool: string
    module?: string
    entity?: string
    action?: string
    args: Record<string, unknown>
    confirm?: boolean
  }): Promise<McpActionProposal> {
    return this.t.rpcScalar('execute_mcp_action', {
      p_proposal_id: opts.proposalId ?? null,
      p_tool_name: opts.tool,
      p_module: opts.module ?? null,
      p_entity: opts.entity ?? null,
      p_action: opts.action ?? opts.tool,
      p_args: opts.args,
      p_confirm: opts.confirm ?? false,
    }, 'core')
  }

  /** A registered mutator is advertised only with an explicit atomic pair. */
  mcpAtomicToolAvailable(tool: string): Promise<boolean> {
    return this.t.rpcScalar('mcp_atomic_tool_available', { p_tool_name: tool }, 'core')
  }

  /** Discovery/execution licence answer: reads survive expiry, writes do not. */
  mcpModuleAvailable(module: string, mutates: boolean): Promise<boolean> {
    return this.t.rpcScalar('mcp_module_available', {
      p_module: module,
      p_mutates: mutates,
    }, 'core')
  }

  /** Published instruction policy/steering only. It reads no arbitrary module record table. */
  resolveAiInstructionBundle(opts: {
    module?: string
    entity?: string
    experience?: string
    taskKind?: string
    action?: string
    locale?: string
  } = {}): Promise<AiInstructionBundle> {
    return this.t.rpcScalar<AiInstructionBundle>('resolve_ai_instruction_bundle', {
      p_module: opts.module ?? null,
      p_entity: opts.entity ?? null,
      p_experience: opts.experience ?? null,
      p_task_kind: opts.taskKind ?? null,
      p_action: opts.action ?? null,
      p_locale: opts.locale ?? 'en',
    })
  }

  /**
   * What a screen is ABOUT (core 0.301.0, #1397 slice 5): the module to bias the
   * tool manifest toward and, where there is one, the entity whose guidance and
   * context providers apply. An unregistered kind resolves to itself as the
   * module — byte-for-byte what the relay's compiled KIND map did.
   */
  resolveAiSurface(kind: string): Promise<AiSurface> {
    return this.t.rpcScalar<AiSurface>('resolve_ai_surface', { p_kind: kind })
  }

  /**
   * The whole compiled context envelope for one AI turn (core 0.298.0, #1397 slice 2):
   * published instructions, the record in hand from registered caller-scoped providers,
   * the rules that apply to its module, the governed tool manifest, and a deterministic
   * hash over all four. One RPC, so copilot and MCP cannot compile different envelopes
   * from the same question.
   */
  resolveAiContextBundle(opts: {
    module?: string
    entity?: string
    experience?: string
    taskKind?: string
    action?: string
    locale?: string
    recordId?: string | null
    taskId?: string | null
    triggers?: string[] | null
    preferredModules?: string[]
    toolBudget?: number
    supportedBindKinds?: string[]
    workBudget?: number | null
  } = {}): Promise<AiContextBundle> {
    return this.t.rpcScalar<AiContextBundle>('resolve_ai_context_bundle', {
      p_module: opts.module ?? null,
      p_entity: opts.entity ?? null,
      p_experience: opts.experience ?? null,
      p_task_kind: opts.taskKind ?? null,
      p_action: opts.action ?? null,
      p_locale: opts.locale ?? 'en',
      p_record_id: opts.recordId ?? null,
      p_task_id: opts.taskId ?? null,
      p_triggers: opts.triggers ?? null,
      p_preferred_modules: opts.preferredModules ?? [],
      p_tool_budget: opts.toolBudget ?? 48,
      p_supported_bind_kinds: opts.supportedBindKinds ?? ['rpc', 'entity', 'builtin'],
      p_work_budget: opts.workBudget ?? null,
    })
  }

  /** Declared settings + current values (core 0034), for a module or all. Secrets
   *  are masked; the raw secret is never shipped. Drives the generic Settings UI. */
  describeSettings(module?: string): Promise<SettingRow[]> {
    return this.t.rpcScalar<SettingRow[]>('describe_settings', { p_module: module ?? null })
  }
  /** Read one setting's value (or its declared default); secrets → null for non-admins. */
  getSetting<T = unknown>(module: string, key: string): Promise<T> {
    return this.t.rpcScalar<T>('get_setting', { p_module: module, p_key: key })
  }
  /** Write one setting (platform-admin only, enforced server-side; audited). */
  setSetting(module: string, key: string, value: unknown): Promise<void> {
    return this.t.rpcScalar<void>('set_setting', { p_module: module, p_key: key, p_value: value })
  }

  /** Relationship authority configuration, with the module preset retained. */
  describeRelationshipBehaviours(): Promise<RelationshipBehaviourDescription> {
    return this.t.rpcScalar<RelationshipBehaviourDescription>('describe_relationship_behaviours')
  }
  setRelationshipBehaviour(
    childEntityType: string,
    fkColumn: string,
    values: Pick<RelationshipBehaviourRow, 'kind' | 'write_requires' | 'on_parent_delete' | 'reparent'>,
    reason: string,
  ): Promise<void> {
    return this.t.rpcScalar<void>('set_relationship_behaviour', {
      p_child_entity_type: childEntityType,
      p_fk_column: fkColumn,
      p_kind: values.kind,
      p_write_requires: values.write_requires,
      p_on_parent_delete: values.on_parent_delete,
      p_reparent: values.reparent,
      p_reason: reason,
    })
  }
  resetRelationshipBehaviour(childEntityType: string, fkColumn: string, reason: string): Promise<void> {
    return this.t.rpcScalar<void>('reset_relationship_behaviour', {
      p_child_entity_type: childEntityType,
      p_fk_column: fkColumn,
      p_reason: reason,
    })
  }

  /**
   * The party role vocabulary for the Settings face — every definition, its
   * availability, and how many assignments already carry it.
   *
   * Deliberately UNFILTERED: an archived definition is exactly what this
   * surface exists to show. The PICKERS get their availability from
   * `core.catalog_options`, which returns the same rows with `selectable`.
   */
  describePartyRoleTypes(): Promise<PartyRoleTypeRow[]> {
    return this.t.rpcScalar<PartyRoleTypeRow[]>('describe_party_role_types')
  }

  /**
   * Archive or restore a party role type. Platform admin, asserted in the body
   * of the definer; the table policy is the backstop and the BEFORE trigger on
   * `core.party_role` is what actually refuses a new assignment.
   */
  setPartyRoleTypeAvailability(key: string, status: PartyRoleTypeStatus, reason?: string): Promise<void> {
    return this.t.rpcScalar<void>('set_party_role_type_availability', {
      p_key: key,
      p_status: status,
      p_reason: reason ?? null,
    })
  }

  /**
   * The resolved dashboard for a host and placement, in one round trip
   * (#1188 slice 2): provenance, effective ordered items after this caller's
   * own sparse patch, and each item's availability.
   *
   * The shape is deliberately opaque here — `parseDescribedDashboard` in
   * `@solum/experience` is the ONE reader, so the console and the portal cannot
   * drift into two interpretations of the same wire format.
   */
  describeDashboard(host: string, placement = 'home', persona?: string): Promise<unknown> {
    return this.t.rpcScalar<unknown>('describe_dashboard', {
      p_host: host, p_placement: placement, p_persona: persona ?? null,
    })
  }

  /**
   * Resolve one governed page LayoutDocument and all referenced dashboard
   * widgets in one caller-RLS round trip. The result stays opaque here:
   * `parseDescribedPage` in @solum/experience is the sole wire reader.
   */
  describePage(host: 'console' | 'portal', pageKey: string, persona?: string): Promise<unknown> {
    return this.t.rpcScalar<unknown>('describe_page_experience', {
      p_host: host, p_page_key: pageKey, p_persona: persona ?? null,
    })
  }

  /**
   * Dry-run the exact database publication boundary without writing a rung.
   * Visual editors, AI and MCP use this before preview/publish; a green local
   * shape check can therefore never disagree with `set_page_experience`.
   */
  validatePageExperience(
    host: 'console' | 'portal', pageKey: string, document: Record<string, unknown>,
  ): Promise<{ valid: boolean; issues: string[] }> {
    return this.t.rpcScalar<{ valid: boolean; issues: string[] }>('validate_page_definition', {
      p_host: host, p_page_key: pageKey, p_document: document,
    })
  }

  /** The caller-visible identities an editor, AI or MCP client may place on
   * one page. It contains metadata only; no record values travel here. */
  describePageCatalog(host: 'console' | 'portal', pageKey: string): Promise<unknown> {
    return this.t.rpcScalar<unknown>('describe_page_catalog', {
      p_host: host, p_page_key: pageKey,
    })
  }

  /** Read the selected editable tenant/persona layer beside the effective
   * resolved page. Platform-admin only; `version` is the optimistic-write key. */
  getPageDefinition(host: 'console' | 'portal', pageKey: string, persona?: string): Promise<unknown> {
    return this.t.rpcScalar<unknown>('get_page_definition', {
      p_host: host, p_page_key: pageKey, p_persona: persona ?? null,
    })
  }

  /** Describe a candidate through the production renderer boundary without
   * storing it. A foreign-persona preview remains platform-admin only. */
  previewPageDefinition(
    host: 'console' | 'portal', pageKey: string,
    document: Record<string, unknown>, persona?: string,
  ): Promise<unknown> {
    return this.t.rpcScalar<unknown>('preview_page_definition', {
      p_host: host, p_page_key: pageKey, p_document: document,
      p_persona: persona ?? null,
    })
  }

  /** Publish one exact tenant/persona layer. The database compares the version
   * under lock and appends only document hashes to the immutable audit chain. */
  publishPageDefinition(
    host: 'console' | 'portal', pageKey: string, document: Record<string, unknown>,
    expectedVersion: number, persona?: string,
  ): Promise<unknown> {
    return this.t.rpcScalar<unknown>('publish_page_definition', {
      p_host: host, p_page_key: pageKey, p_document: document,
      p_persona: persona ?? null, p_expected_version: expectedVersion,
    })
  }

  /** Remove only the exact editable layer after an optimistic version check,
   * revealing the governed lower rung. */
  resetPageDefinition(
    host: 'console' | 'portal', pageKey: string, expectedVersion: number, persona?: string,
  ): Promise<unknown> {
    return this.t.rpcScalar<unknown>('reset_page_definition', {
      p_host: host, p_page_key: pageKey, p_persona: persona ?? null,
      p_expected_version: expectedVersion,
    })
  }

  /** Author this deployment's tenant or persona page layer. Authority and the
   * closed LayoutDocument grammar are both enforced by the database door. */
  setPageExperience(
    host: 'console' | 'portal', pageKey: string,
    document: Record<string, unknown>, persona?: string,
  ): Promise<void> {
    return this.t.rpcScalar<void>('set_page_experience', {
      p_host: host, p_page_key: pageKey, p_document: document,
      p_persona: persona ?? null,
    })
  }

  /** Delete only this deployment's selected layer, revealing the lower rung. */
  clearPageExperience(
    host: 'console' | 'portal', pageKey: string, persona?: string,
  ): Promise<void> {
    return this.t.rpcScalar<void>('clear_page_experience', {
      p_host: host, p_page_key: pageKey, p_persona: persona ?? null,
    })
  }

  /** Store this caller's sparse layout patch. The grammar is closed and the
   *  server refuses anything outside it — reorder, hide, resize, collapse and
   *  personal parameters. A content change creates a scope=user dashboard. */
  setDashboardLayout(dashboardId: string, document: Record<string, unknown>): Promise<void> {
    return this.t.rpcScalar<void>('set_dashboard_layout', {
      p_dashboard_id: dashboardId, p_document: document,
    })
  }

  /** Remove this caller's patch, revealing the rung below. Deleting rather than
   *  emptying is what keeps "no preference" distinct from "show nothing". */
  resetDashboardLayout(dashboardId: string): Promise<void> {
    return this.t.rpcScalar<void>('reset_dashboard_layout', { p_dashboard_id: dashboardId })
  }

  pinDashboard(host: string, placement: string, dashboardId: string): Promise<void> {
    return this.t.rpcScalar<void>('pin_dashboard', {
      p_host: host, p_placement: placement, p_dashboard_id: dashboardId,
    })
  }

  unpinDashboard(host: string, placement: string): Promise<void> {
    return this.t.rpcScalar<void>('unpin_dashboard', { p_host: host, p_placement: placement })
  }

  /** Resolve an experience-layer L1 document (most-specific-wins), or null ⇒ use the L0 auto view. */
  getExperience(entity: string, kind: ExperienceKind, opts?: { persona?: string; host?: string }): Promise<ExperienceDocument | null> {
    return this.t.rpcScalar<ExperienceDocument | null>('get_experience', {
      p_entity_type: entity, p_kind: kind, p_persona: opts?.persona ?? null, p_host: opts?.host ?? null,
    })
  }

  /**
   * Resolve one governed presentation through the server-owned cascade (#2032).
   *
   * Ordinary hosts omit `persona`: the server derives the caller's active
   * assignment. Supplying it is an explicit administrative preview request and
   * the database refuses it for a caller who is not allowed to preview another
   * persona. The result stays `unknown` here because @solum/experience is the
   * one versioned wire parser; making the SDK depend on it would create the
   * existing experience -> sdk dependency in reverse.
   */
  resolveExperience(
    entity: string,
    artifact: string,
    opts?: { host?: string; previewPersona?: string },
  ): Promise<unknown> {
    return this.t.rpcScalar<unknown>('resolve_experience', {
      p_entity: entity,
      p_artifact: artifact,
      p_host: opts?.host ?? null,
      p_preview_persona: opts?.previewPersona ?? null,
    })
  }

  /** Offline entitlement check (module, optional feature). Absent ⇒ false, never throws. */
  entitled(module: string, feature?: string): Promise<boolean> {
    return this.t.rpcScalar<boolean>('entitlement', { p_module: module, p_feature: feature ?? null })
  }

  /**
   * A handle built from an entity row the host ALREADY holds — typically one
   * of `describeSchema().entities` — with no `core.entity_type` round trip.
   * The row is learned like any other registry read, so `raw()` agrees with
   * the handle about the table's doors.
   */
  handleFor(info: EntityTypeInfo): EntityHandle {
    this.learnWriteDoors([info])
    return new EntityHandle(this.t, info, this.opts.privilege ?? 'api')
  }

  /** Tool-discovery entitlement, including closure over unsellable bridge endpoints. */
  moduleDiscoverable(module: string): Promise<boolean> {
    return this.t.rpcScalar<boolean>('module_discoverable', { p_module: module })
  }

  /** A handle to any registered entity by its metamodel key, or null if unknown. */
  async entity(typeKey: string): Promise<EntityHandle | null> {
    const rows = await this.t.select<EntityTypeInfo>('core', 'entity_type', { where: { key: typeKey } })
    // The row that answers the entity path answers the raw one too: a host that
    // only ever touches one entity still gets that table's door.
    this.learnWriteDoors(rows)
    return rows[0] ? new EntityHandle(this.t, rows[0], this.opts.privilege ?? 'api') : null
  }

  /** A module's surface — `null` (absent) when the license does not entitle it. */
  async module(name: string): Promise<ModuleHandle | null> {
    if (!(await this.entitled(name))) return null
    const desc = await this.describeSchema()
    return new ModuleHandle(this.t, name, desc.entities.filter((e) => e.module === name), this.opts.privilege ?? 'api')
  }
}

export function createClient(transport: Transport, opts: ClientOptions = {}): SolumClient {
  return new SolumClient(transport, opts)
}
